import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, Building2, Globe, CheckCheck, CheckCircle2, XCircle, Image as ImageIcon, Download, Eye, Gauge, Loader2, Mail, ScrollText, Search, Settings2,
  Shield, Users, Wrench, Megaphone, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { apiError, enterWorkspaceAsAdmin } from "../../lib/auth";
import { useSession } from "../../lib/session";
import { Toaster } from "../ui/sonner";
import { Switch } from "../ui/switch";
import { Mark } from "../../marketing/Brand";

const SECTIONS = [
  ["overview", "Overview", Gauge],
  ["approvals", "Approvals", CheckCheck],
  ["workspaces", "Workspaces", Building2],
  ["users", "Users", Users],
  ["platform", "Platform", Settings2],
  ["waitlist", "Waitlist", Mail],
  ["audit", "Audit log", ScrollText],
];

const inputCls = "w-full bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-fuchsia-500/50";
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const Title = ({ children, sub }) => (
  <div className="mb-8">
    <h1 className="font-heading text-3xl font-extrabold tracking-tight text-white">{children}</h1>
    {sub && <p className="mt-1.5 text-sm text-slate-400">{sub}</p>}
  </div>
);

/* ------------------------------ Overview ------------------------------ */
/* Headline counts are stat tiles, not charts. The figure is set in the body
   sans, never the display face, and wears text colour - not a series colour. */
const Stat = ({ label, value, sub }) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
    <p className="text-xs font-medium text-slate-400">{label}</p>
    <p className="mt-2 text-4xl font-bold text-slate-50 font-sans">{value ?? "—"}</p>
    {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
  </div>
);

/* Magnitude across a few labelled categories: one hue, thin bars with rounded
   ends, the value as text beside each bar so nothing depends on colour or on
   hovering. The hover title repeats it with the share. */
const BarList = ({ rows, total }) => {
  const max = Math.max(1, ...rows.map((r) => r[1]));
  if (!rows.length) return <p className="text-sm text-slate-500">Nothing yet.</p>;
  return (
    <div className="space-y-3">
      {rows.map(([label, n]) => (
        <div key={label} title={`${label}: ${n}${total ? ` (${Math.round((n / total) * 100)}%)` : ""}`}>
          <div className="flex justify-between text-sm mb-1.5">
            <span className="text-slate-300">{label}</span>
            <span className="text-slate-100 font-medium">{n}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-800/70">
            <div className="h-2 rounded-full bg-indigo-400" style={{ width: `${(n / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const STATUS_LABEL = {
  posted: "Published", scheduled: "Scheduled", pending_approval: "Awaiting approval", draft: "Draft",
  generating: "Generating", failed: "Publish failed", rejected: "Rejected", approved: "Approved",
};

const Overview = ({ data, onToggle, busy }) => {
  const statusRows = Object.entries(data.posts_by_status || {})
    .map(([k, v]) => [STATUS_LABEL[k] || k, v]).sort((a, b) => b[1] - a[1]);
  const planRows = Object.entries(data.plans || {}).map(([k, v]) => [k[0].toUpperCase() + k.slice(1), v])
    .sort((a, b) => b[1] - a[1]);
  const totalPosts = statusRows.reduce((s, r) => s + r[1], 0);
  const p = data.platform || {};
  return (
    <>
      <Title sub="Everything running on AutoCruise, at a glance.">Overview</Title>

      <div className="grid gap-3 md:grid-cols-2 mb-8">
        <ToggleCard icon={Wrench} title="Maintenance mode" on={p.maintenance} busy={busy === "maintenance"}
          onLabel="On - visitors see the maintenance page, only you can sign in"
          offLabel="Off - the site and sign-in are open"
          onChange={(v) => onToggle("maintenance", v)} warn />
        <ToggleCard icon={Users} title="Public sign-ups" on={p.signups_open} busy={busy === "signups_open"}
          onLabel={p.maintenance ? "Open, but blocked while maintenance is on" : "Open - anyone can create a workspace"}
          offLabel="Closed - invites and the waitlist only"
          onChange={(v) => onToggle("signups_open", v)} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <Stat label="Users" value={data.users} sub={`${data.users_7d} new this week`} />
        <Stat label="Workspaces" value={data.workspaces} sub={data.suspended ? `${data.suspended} suspended` : "none suspended"} />
        <Stat label="Posts this month" value={data.posts_this_month} />
        <Stat label="Images this month" value={data.images_this_month} />
        <Stat label="Waitlist" value={data.waitlist} />
        <Stat label="All posts" value={totalPosts} />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
          <h3 className="font-heading font-bold text-slate-100 mb-5">Posts by status</h3>
          <BarList rows={statusRows} total={totalPosts} />
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
          <h3 className="font-heading font-bold text-slate-100 mb-5">Workspaces by plan</h3>
          <BarList rows={planRows} total={data.workspaces} />
        </div>
      </div>
    </>
  );
};

const ToggleCard = ({ icon: Icon, title, on, onLabel, offLabel, onChange, busy, warn }) => (
  <div className={`rounded-2xl border p-5 flex items-center gap-4 ${on && warn ? "border-amber-500/30 bg-amber-500/[0.06]" : "border-slate-800 bg-slate-900/40"}`}>
    <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${on && warn ? "bg-amber-500/15 text-amber-300" : "bg-slate-800 text-slate-300"}`}>
      <Icon className="w-5 h-5" />
    </span>
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-slate-100">{title}</p>
      <p className="text-xs text-slate-400 mt-0.5">{on ? onLabel : offLabel}</p>
    </div>
    {busy ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : <Switch checked={Boolean(on)} onCheckedChange={onChange} />}
  </div>
);


/* ------------------------------ Approvals ------------------------------ */
/* Every post waiting for a decision, across all workspaces. Each is decided
   inside its own workspace server-side, exactly as its owner would. */
const Approvals = () => {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(() => new Set());
  const [busy, setBusy] = useState("");
  const [ws, setWs] = useState("");
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/approvals");
    setRows(data);
    setSel(new Set());
  }, []);
  useEffect(() => { load().catch(() => toast.error("Could not load approvals")); }, [load]);

  const visible = (rows || []).filter((r) => !ws || r.workspace_id === ws);
  const workspaces = [...new Map((rows || []).map((r) => [r.workspace_id, r.workspace_name])).entries()];
  const toggle = (id) => setSel((c) => { const n = new Set(c); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const decide = async (action) => {
    setBusy(action);
    try {
      const { data } = await api.post("/admin/approvals/bulk", { ids: [...sel], action });
      toast.success(`${action === "approve" ? "Approved" : "Rejected"} ${data.done.length}`);
      if (data.errors.length) toast.error(`${data.errors.length} failed: ${data.errors[0].error}`);
      await load();
    } catch (err) { toast.error(apiError(err, "Bulk action failed")); }
    finally { setBusy(""); }
  };

  return (
    <>
      <Title sub="Everything waiting for a decision, in every workspace.">Approvals</Title>
      {!rows ? <Spin /> : rows.length === 0 ? <Empty>Nothing waiting anywhere.</Empty> : (
        <>
          <div className="sticky top-2 z-20 mb-4 rounded-2xl border border-slate-800 bg-[#0b0d16]/95 backdrop-blur px-4 py-3 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" className="accent-fuchsia-500 w-4 h-4"
                checked={visible.length > 0 && visible.every((r) => sel.has(r.id))}
                onChange={(e) => setSel(e.target.checked ? new Set(visible.map((r) => r.id)) : new Set())} />
              Select all {visible.length}
            </label>
            <select value={ws} onChange={(e) => { setWs(e.target.value); setSel(new Set()); }}
              className="bg-slate-950/60 border border-slate-800 rounded-lg px-2.5 py-1.5 text-xs text-slate-200">
              <option value="">All workspaces</option>
              {workspaces.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
            <span className="text-xs text-slate-500">{sel.size} selected</span>
            <div className="ml-auto flex gap-2">
              <button onClick={() => decide("approve")} disabled={!sel.size || !!busy}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-200 text-xs font-semibold disabled:opacity-40">
                {busy === "approve" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Approve selected
              </button>
              <button onClick={() => decide("reject")} disabled={!sel.size || !!busy}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-500/15 text-rose-300 text-xs font-semibold disabled:opacity-40">
                {busy === "reject" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />} Reject selected
              </button>
            </div>
          </div>
          <div className="grid gap-3 lg:grid-cols-2">
            {visible.map((r) => {
              const img = r.image_url || r.slide_url;
              const on = sel.has(r.id);
              return (
                <label key={r.id} className={`flex gap-3 rounded-2xl border p-3 cursor-pointer transition-colors ${
                  on ? "border-fuchsia-400/60 bg-fuchsia-500/[0.06]" : "border-slate-800 bg-slate-900/40 hover:border-slate-700"}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(r.id)} className="accent-fuchsia-500 w-4 h-4 mt-1" />
                  <div className="w-20 h-20 rounded-xl overflow-hidden bg-slate-950 shrink-0 flex items-center justify-center">
                    {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-slate-600" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold text-fuchsia-300">{r.workspace_name}</p>
                    <p className="text-sm font-semibold text-slate-100 truncate">{r.topic}</p>
                    <p className="text-xs text-slate-400 line-clamp-2 mt-0.5">{r.caption}</p>
                    <p className="text-[11px] text-slate-500 mt-1">
                      {r.fmt} · {(r.platforms || []).join(", ")}{r.scheduled_at ? ` · ${fmtTime(r.scheduled_at)}` : ""}
                    </p>
                    {r.hold_reasons?.length > 0 && <p className="text-[11px] text-amber-300/90 mt-1 truncate">Held: {r.hold_reasons[0]}</p>}
                  </div>
                </label>
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

/* ------------------------------ Workspaces ------------------------------ */
const Workspaces = () => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [plans, setPlans] = useState({});
  const load = useCallback(async () => {
    const [w, p] = await Promise.all([api.get(`/admin/workspaces?q=${encodeURIComponent(q)}`), api.get("/admin/plans")]);
    setRows(w.data);
    setPlans(p.data);
  }, [q]);
  useEffect(() => { load().catch(() => toast.error("Could not load workspaces")); }, [load]);

  const update = async (id, patch, msg) => {
    try {
      await api.patch(`/admin/workspaces/${id}`, patch);
      toast.success(msg);
      load();
    } catch (err) { toast.error(apiError(err, "Update failed")); }
  };
  const enter = async (id) => {
    try {
      await enterWorkspaceAsAdmin(id);
      window.location.assign("/app");
    } catch (err) { toast.error(apiError(err, "Could not enter the workspace")); }
  };

  return (
    <>
      <Title sub="Every customer workspace. Change a plan, suspend, or step inside to help - every entry is logged.">Workspaces</Title>
      <SearchBox value={q} onChange={setQ} placeholder="Search by name" />
      {!rows ? <Spin /> : (
        <div className="overflow-x-auto rounded-2xl border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-xs text-slate-400">
              <tr>{["Workspace", "Owner", "Plan", "Members", "Accounts", "Posts / mo", "Images / mo", "Created", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-medium whitespace-nowrap">{h}</th>))}</tr>
            </thead>
            <tbody className="divide-y divide-slate-800/80">
              {rows.map((w) => (
                <tr key={w.id} className="hover:bg-slate-900/40">
                  <td className="px-4 py-3 font-semibold text-slate-100 whitespace-nowrap">{w.name}</td>
                  <td className="px-4 py-3 text-slate-400">{w.owner_email}</td>
                  <td className="px-4 py-3">
                    <select value={w.plan || "free"} onChange={(e) => update(w.id, { plan: e.target.value }, `Moved to ${e.target.value}`)}
                      className="bg-slate-950/60 border border-slate-800 rounded-lg px-2 py-1 text-xs text-slate-200">
                      {Object.keys(plans).map((k) => <option key={k} value={k}>{plans[k].label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{w.members}</td>
                  <td className="px-4 py-3 text-slate-300">{w.accounts}</td>
                  <td className="px-4 py-3 text-slate-300">{w.posts_this_month}</td>
                  <td className="px-4 py-3 text-slate-300">{w.images_this_month}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(w.created_at)}</td>
                  <td className="px-4 py-3">
                    <Switch checked={w.status !== "suspended"}
                      onCheckedChange={(v) => update(w.id, { status: v ? "active" : "suspended" }, v ? "Reactivated" : "Suspended")} />
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => enter(w.id)} className="flex items-center gap-1.5 text-xs font-semibold text-fuchsia-300 hover:text-fuchsia-200 whitespace-nowrap">
                      <Eye className="w-3.5 h-3.5" /> Enter
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

/* ------------------------------ Users ------------------------------ */
const UsersSection = () => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/users?q=${encodeURIComponent(q)}`);
    setRows(data);
  }, [q]);
  useEffect(() => { load().catch(() => toast.error("Could not load users")); }, [load]);
  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/users/${id}`, { status });
      toast.success(status === "suspended" ? "User suspended" : "User reactivated");
      load();
    } catch (err) { toast.error(apiError(err, "Update failed")); }
  };
  return (
    <>
      <Title sub="Suspending someone signs them out within seconds and blocks sign-in.">Users</Title>
      <SearchBox value={q} onChange={setQ} placeholder="Search by name or email" />
      {!rows ? <Spin /> : (
        <div className="overflow-x-auto rounded-2xl border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-xs text-slate-400">
              <tr>{["Name", "Email", "Role", "Workspaces", "Last sign-in", "Joined", "Active"].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-medium whitespace-nowrap">{h}</th>))}</tr>
            </thead>
            <tbody className="divide-y divide-slate-800/80">
              {rows.map((u) => (
                <tr key={u.id} className="hover:bg-slate-900/40">
                  <td className="px-4 py-3 font-semibold text-slate-100">{u.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-400">{u.email || u.username}</td>
                  <td className="px-4 py-3">
                    {u.role === "super_admin"
                      ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-fuchsia-300"><Shield className="w-3.5 h-3.5" /> Super admin</span>
                      : <span className="text-xs text-slate-400">Customer</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-300">{u.workspaces}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtTime(u.last_login_at)}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3">
                    {u.role !== "super_admin" && (
                      <Switch checked={u.status !== "suspended"} onCheckedChange={(v) => setStatus(u.id, v ? "active" : "suspended")} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

/* ------------------------------ Platform ------------------------------ */
const PlatformSection = ({ onSaved }) => {
  const [p, setP] = useState(null);
  const [oauth, setOauth] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/platform").then(({ data }) => setP(data));
    api.get("/oauth/config").then(({ data }) => setOauth(data)).catch(() => {});
  }, []);
  if (!p) return <Spin />;
  const set = (k, v) => setP({ ...p, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/platform", p);
      setP(data);
      toast.success("Platform settings saved");
      onSaved();
    } catch (err) { toast.error(apiError(err, "Could not save")); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Title sub="Switches that apply to every workspace.">Platform</Title>
      <div className="grid gap-5 lg:grid-cols-2">
        <Card icon={Wrench} title="Maintenance">
          <Row label="Maintenance mode" hint="Visitors see the maintenance page. Customers are paused. Only you can sign in, with the PIN.">
            <Switch checked={Boolean(p.maintenance)} onCheckedChange={(v) => set("maintenance", v)} />
          </Row>
          <label className="block text-xs font-semibold text-slate-400 mb-1.5 mt-4">Message on the maintenance page</label>
          <textarea rows={3} value={p.maintenance_message || ""} onChange={(e) => set("maintenance_message", e.target.value)} className={inputCls} />
        </Card>

        <Card icon={Users} title="Access">
          <Row label="Public sign-ups" hint="When off, people join only by invite or from the waitlist.">
            <Switch checked={Boolean(p.signups_open)} onCheckedChange={(v) => set("signups_open", v)} />
          </Row>
          <label className="block text-xs font-semibold text-slate-400 mb-1.5 mt-4">Product name</label>
          <input value={p.product_name || ""} onChange={(e) => set("product_name", e.target.value)} className={inputCls} />
        </Card>

        <Card icon={Megaphone} title="Announcement banner">
          <p className="text-xs text-slate-500 mb-3">Shown at the top of every workspace. Leave empty to hide it.</p>
          <textarea rows={2} value={p.announcement || ""} onChange={(e) => set("announcement", e.target.value)}
            placeholder="e.g. Instagram publishing is delayed - we're on it." className={inputCls} />
          <div className="mt-3 flex gap-2">
            {["info", "warning", "success"].map((lvl) => (
              <button key={lvl} onClick={() => set("announcement_level", lvl)}
                className={`px-3 py-1.5 rounded-lg border text-xs font-semibold capitalize ${p.announcement_level === lvl ? "border-fuchsia-400/60 bg-fuchsia-500/10 text-fuchsia-100" : "border-slate-800 text-slate-400"}`}>
                {lvl}
              </button>
            ))}
          </div>
        </Card>

        <Card icon={KeyRound} title="Social app credentials">
          <p className="text-xs text-slate-500 mb-3">One LinkedIn app and one Meta app for the whole platform - every workspace connects its own accounts through them.</p>
          <div className="grid grid-cols-2 gap-2">
            <input value={p.linkedin_client_id || ""} onChange={(e) => set("linkedin_client_id", e.target.value)} placeholder="LinkedIn client ID" className={inputCls} />
            <input value={p.linkedin_client_secret || ""} onChange={(e) => set("linkedin_client_secret", e.target.value)} placeholder="LinkedIn secret" className={inputCls} />
            <input value={p.meta_app_id || ""} onChange={(e) => set("meta_app_id", e.target.value)} placeholder="Meta app ID" className={inputCls} />
            <input value={p.meta_app_secret || ""} onChange={(e) => set("meta_app_secret", e.target.value)} placeholder="Meta app secret" className={inputCls} />
            <input value={p.threads_app_id || ""} onChange={(e) => set("threads_app_id", e.target.value)} placeholder="Threads app ID" className={inputCls} />
            <input value={p.threads_app_secret || ""} onChange={(e) => set("threads_app_secret", e.target.value)} placeholder="Threads app secret" className={inputCls} />
            <input value={p.linkedin_pages_client_id || ""} onChange={(e) => set("linkedin_pages_client_id", e.target.value)} placeholder="LinkedIn Pages client ID (optional)" className={inputCls} />
            <input value={p.linkedin_pages_client_secret || ""} onChange={(e) => set("linkedin_pages_client_secret", e.target.value)} placeholder="LinkedIn Pages secret (optional)" className={inputCls} />
          </div>
          <label className="mt-3 flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={!!p.linkedin_includes_pages} onChange={(e) => set("linkedin_includes_pages", e.target.checked)}
              className="accent-fuchsia-500 mt-0.5" />
            <span className="text-xs text-slate-300">
              The LinkedIn app above has the Community Management API - one sign-in connects profile + pages.
              <span className="block text-[11px] text-slate-500">Leave the LinkedIn Pages fields blank in that case.</span>
            </span>
          </label>
          {oauth && (
            <div className="mt-3 space-y-1 text-[11px] text-slate-500">
              <p>LinkedIn redirect: <code className="text-slate-300">{oauth.linkedin_redirect_uri}</code></p>
              <p>Meta redirect: <code className="text-slate-300">{oauth.meta_redirect_uri}</code></p>
              <p>Threads redirect: <code className="text-slate-300">{oauth.threads_redirect_uri}</code></p>
              <p>LinkedIn Pages redirect: <code className="text-slate-300">{oauth.linkedin_pages_redirect_uri}</code></p>
              <p>Meta deauthorize: <code className="text-slate-300">{oauth.meta_deauthorize_uri}</code></p>
              <p>Meta data deletion: <code className="text-slate-300">{oauth.meta_delete_uri}</code></p>
              <p>Threads uninstall: <code className="text-slate-300">{oauth.threads_uninstall_uri}</code></p>
              <p>Threads delete: <code className="text-slate-300">{oauth.threads_delete_uri}</code></p>
            </div>
          )}
        </Card>
      </div>
      <div className="mt-6 flex justify-end">
        <button onClick={save} disabled={saving}
          className="px-6 py-2.5 rounded-xl bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-sm font-semibold flex items-center gap-2">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save platform settings
        </button>
      </div>
    </>
  );
};

const Card = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
    <h3 className="font-heading font-bold text-slate-100 flex items-center gap-2 mb-4"><Icon className="w-4 h-4 text-fuchsia-300" /> {title}</h3>
    {children}
  </div>
);

const Row = ({ label, hint, children }) => (
  <div className="flex items-start gap-4">
    <div className="flex-1">
      <p className="text-sm font-semibold text-slate-200">{label}</p>
      <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
    </div>
    {children}
  </div>
);

/* ------------------------------ Waitlist & audit ------------------------------ */
const Waitlist = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/waitlist").then(({ data }) => setRows(data)); }, []);
  const exportCsv = () => {
    const csv = ["email,name,company,joined_at", ...rows.map((r) =>
      [r.email, r.name, r.company, r.joined_at].map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: "autocruise-waitlist.csv" });
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <Title sub="People who asked for early access.">Waitlist</Title>
        {rows?.length > 0 && (
          <button onClick={exportCsv} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-700 text-sm font-semibold text-slate-200 hover:border-slate-600">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        )}
      </div>
      {!rows ? <Spin /> : rows.length === 0 ? <Empty>No one yet.</Empty> : (
        <div className="overflow-x-auto rounded-2xl border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-xs text-slate-400"><tr>{["Email", "Name", "Company", "Joined"].map((h) => <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-800/80">
              {rows.map((r) => (
                <tr key={r.email}><td className="px-4 py-3 text-slate-100">{r.email}</td><td className="px-4 py-3 text-slate-400">{r.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-400">{r.company || "—"}</td><td className="px-4 py-3 text-slate-500">{fmtTime(r.joined_at)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

const AUDIT_LABEL = {
  signup: "Signed up", "invite.create": "Created an invite", "member.role": "Changed a role", "member.remove": "Removed a member",
  "workspace.delete": "Deleted a workspace", "admin.workspace.update": "Updated a workspace", "admin.workspace.enter": "Entered a workspace",
  "admin.user.status": "Changed a user's status", "admin.platform.maintenance": "Toggled maintenance", "admin.platform.signups_open": "Toggled sign-ups",
};

const Audit = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/audit").then(({ data }) => setRows(data)); }, []);
  return (
    <>
      <Title sub="Who did what. Append-only.">Audit log</Title>
      {!rows ? <Spin /> : rows.length === 0 ? <Empty>Nothing logged yet.</Empty> : (
        <div className="rounded-2xl border border-slate-800 divide-y divide-slate-800/80">
          {rows.map((r) => (
            <div key={r.id} className="flex items-start gap-4 px-5 py-3.5">
              <span className="mt-1 w-2 h-2 rounded-full bg-fuchsia-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-200"><span className="font-semibold">{r.actor_email || "system"}</span> · {AUDIT_LABEL[r.action] || r.action}
                  {r.target && <span className="text-slate-400"> · {r.target}</span>}</p>
                {r.detail && <p className="text-xs text-slate-500 mt-0.5 truncate">{r.detail}</p>}
              </div>
              <span className="text-xs text-slate-500 whitespace-nowrap">{fmtTime(r.at)}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const SearchBox = ({ value, onChange, placeholder }) => (
  <div className="relative mb-5 max-w-sm">
    <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} pl-10`} />
  </div>
);
const Spin = () => <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-fuchsia-300" /></div>;
const Empty = ({ children }) => <div className="rounded-2xl border border-dashed border-slate-800 py-16 text-center text-sm text-slate-500">{children}</div>;

/* ------------------------------ Shell ------------------------------ */
export default function AdminConsole() {
  const { section: raw } = useParams();
  const section = SECTIONS.some(([k]) => k === raw) ? raw : "overview";
  const navigate = useNavigate();
  const { me, refresh } = useSession();
  const [overview, setOverview] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { document.title = "Admin · AutoCruise"; }, []);
  const loadOverview = useCallback(() => api.get("/admin/overview").then(({ data }) => setOverview(data)), []);
  useEffect(() => { if (section === "overview") loadOverview().catch(() => toast.error("Could not load the overview")); }, [section, loadOverview]);

  const toggle = async (key, value) => {
    setBusy(key);
    try {
      await api.put("/admin/platform", { [key]: value });
      await loadOverview();
      await refresh();
      toast.success(key === "maintenance" ? (value ? "Maintenance on" : "Site is live") : (value ? "Sign-ups open" : "Sign-ups closed"));
    } catch (err) { toast.error(apiError(err, "Could not change that")); }
    finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen bg-[#08090f] text-slate-200 flex">
      <Toaster position="top-right" theme="dark" richColors />
      <aside className="hidden md:flex flex-col w-60 shrink-0 h-screen sticky top-0 border-r border-slate-800/80 bg-[#06070c] px-4 py-6">
        <div className="flex items-center gap-2.5 px-2">
          <Mark className="w-7 h-7" />
          <div className="leading-tight">
            <p className="font-heading font-extrabold text-[15px] text-white">AutoCruise</p>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-fuchsia-300">Super admin</p>
          </div>
        </div>
        <nav className="mt-9 flex flex-col gap-1">
          {SECTIONS.map(([key, label, Icon]) => (
            <button key={key} onClick={() => navigate(key === "overview" ? "/admin" : `/admin/${key}`)}
              className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                section === key ? "bg-fuchsia-500/10 text-white" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/50"}`}>
              {section === key && <motion.span layoutId="admin-pill" className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-full bg-fuchsia-400" />}
              <Icon className="w-[18px] h-[18px]" /> {label}
            </button>
          ))}
        </nav>
        <div className="mt-auto space-y-2">
          <p className="px-3 text-xs text-slate-500 truncate">{me?.user?.email || me?.user?.username}</p>
          <button onClick={() => navigate("/app")} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-slate-800/50">
            <ArrowLeft className="w-4 h-4" /> Back to my workspace
          </button>
          <button onClick={() => window.open("/", "_blank")} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-slate-800/50">
            <Globe className="w-4 h-4" /> View public site
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="md:hidden flex gap-1 overflow-x-auto px-3 py-2 border-b border-slate-800 bg-[#06070c]">
          {SECTIONS.map(([key, label]) => (
            <button key={key} onClick={() => navigate(key === "overview" ? "/admin" : `/admin/${key}`)}
              className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-semibold ${section === key ? "bg-fuchsia-500/15 text-fuchsia-200" : "text-slate-400"}`}>{label}</button>
          ))}
          <button onClick={() => navigate("/app")} className="ml-auto whitespace-nowrap px-3 py-1.5 text-xs text-slate-400">← App</button>
        </div>
        <main className="px-4 sm:px-10 py-8 sm:py-12 max-w-[1300px]">
          <AnimatePresence mode="wait">
            <motion.div key={section} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              {section === "overview" && (overview ? <Overview data={overview} onToggle={toggle} busy={busy} /> : <Spin />)}
              {section === "approvals" && <Approvals />}
              {section === "workspaces" && <Workspaces />}
              {section === "users" && <UsersSection />}
              {section === "platform" && <PlatformSection onSaved={refresh} />}
              {section === "waitlist" && <Waitlist />}
              {section === "audit" && <Audit />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
